import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { db } from "@lib/firebase";
import {
  collection,
  query,
  onSnapshot,
  where,
  limit,
  startAfter,
  orderBy,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faCheckCircle,
  faSearch,
  faSortUp,
  faSortDown,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";

const PAGE_SIZE = 50;

const SkeletonRow = React.memo(() => (
  <tr className="border-b border-gray-200">
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4 hidden md:table-cell">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4 hidden md:table-cell">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse w-4"></div>
    </td>
  </tr>
));

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

export const EntryList = React.memo(() => {
  const { eventId, category } = useParams();

  const [state, setState] = useState({
    participants: [],
    allParticipants: [],
    filteredParticipants: [],
    fullFilteredList: [],
    searchTerm: "",
    eventTitle: "",
    eventType: "",
    collectionName: "",
    loading: true,
    loadingMore: false,
    error: null,
    lastVisible: null,
    totalParticipants: 0,
    totalFiltered: 0,
    sortGenderDirection: null,
    hasMore: true,
    searchPageIndex: 0,
    categories: [],
    selectedCategory: "all",
  });

  const isMounted = useRef(true);
  const unsubscribeRef = useRef({
    participants: null,
    total: null,
  });
  const searchPageIndexRef = useRef(0);

  const debouncedSearchTerm = useDebounce(state.searchTerm, 300);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      Object.values(unsubscribeRef.current).forEach((unsubscribe) => {
        if (unsubscribe) unsubscribe();
      });
    };
  }, []);

  const buildDistanceOrCategoryConstraint = (
    baseConstraints,
    selectedDistance
  ) => {
    if (!selectedDistance || selectedDistance === "all") return;
    const sdTrim = String(selectedDistance).trim();
    const numeric = Number(sdTrim);
    if (sdTrim !== "" && !Number.isNaN(numeric)) {
      baseConstraints.push(where("distance", "==", numeric));
    } else {
      baseConstraints.push(where("kategori", "==", sdTrim));
    }
  };

  const loadPage = useCallback(
    (collectionName, lastDoc, sortDirection, selectedCategory) => {
      setState((prev) => ({
        ...prev,
        loading: !lastDoc,
        loadingMore: !!lastDoc,
        participants: lastDoc ? prev.participants : [],
        filteredParticipants: debouncedSearchTerm
          ? prev.filteredParticipants
          : [],
      }));

      const participantsCollection = collection(db, collectionName);
      const baseConstraints = [
        where("transaction_status", "in", ["settlement", "pending"]),
      ];

      buildDistanceOrCategoryConstraint(baseConstraints, selectedCategory);

      const orderConstraints = [];
      if (sortDirection) {
        orderConstraints.push(orderBy("gender", sortDirection));
      }
      orderConstraints.push(orderBy("transaction_status"));
      orderConstraints.push(orderBy("namaLengkap"));

      const qParticipants = lastDoc
        ? query(
            participantsCollection,
            ...baseConstraints,
            ...orderConstraints,
            startAfter(lastDoc),
            limit(PAGE_SIZE)
          )
        : query(
            participantsCollection,
            ...baseConstraints,
            ...orderConstraints,
            limit(PAGE_SIZE)
          );

      const unsubscribe = onSnapshot(
        qParticipants,
        (snapshot) => {
          if (!isMounted.current) return;

          if (snapshot.empty) {
            setState((prev) => ({
              ...prev,
              participants: debouncedSearchTerm ? prev.participants : [],
              filteredParticipants: debouncedSearchTerm
                ? prev.filteredParticipants
                : [],
              lastVisible: null,
              loading: false,
              loadingMore: false,
              hasMore: false,
            }));
            return;
          }

          const participantList = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              nama:
                data.teamName && data.kategori === "Mix Group (20K)"
                  ? data.teamName
                  : data.namaLengkap || "Name not available",
              gender: data.gender || "Unknown",
              kota: data.kota || data.alamat || "Unknown",
              country:
                data.negara || data.country || data.nationality || "Unknown",
              statusPembayaran: data.transaction_status || "pending",
              category: data.kategori || "Unknown",
              docRef: doc,
            };
          });

          setState((prev) => {
            const updatedParticipants = lastDoc
              ? [...prev.participants, ...participantList]
              : participantList;
            return {
              ...prev,
              participants: updatedParticipants,
              filteredParticipants: debouncedSearchTerm
                ? prev.filteredParticipants
                : updatedParticipants,
              lastVisible:
                snapshot.docs.length > 0
                  ? snapshot.docs[snapshot.docs.length - 1]
                  : null,
              loading: false,
              loadingMore: false,
              totalFiltered: debouncedSearchTerm
                ? prev.totalFiltered
                : prev.totalParticipants,
              hasMore: snapshot.size === PAGE_SIZE,
            };
          });
        },
        (err) => {
          if (!isMounted.current) return;
          setState((prev) => ({
            ...prev,
            error: `Failed to load participant data: ${err.message}. Please check Firestore indexes or contact admin.`,
            loading: false,
            loadingMore: false,
          }));
          console.error(`[ERROR] loadPage.onSnapshot: ${err.message}`);
        }
      );

      if (unsubscribeRef.current.participants) {
        try {
          unsubscribeRef.current.participants();
        } catch (e) {
          // ignore
        }
      }
      unsubscribeRef.current.participants = unsubscribe;
    },
    [debouncedSearchTerm]
  );

  const fetchAllParticipants = useCallback(
    async (collectionName, selectedCategory) => {
      try {
        const participantsCollection = collection(db, collectionName);
        const baseConstraints = [
          where("transaction_status", "in", ["settlement", "pending"]),
        ];

        buildDistanceOrCategoryConstraint(baseConstraints, selectedCategory);

        const q = query(participantsCollection, ...baseConstraints);

        const snapshot = await getDocs(q);
        const allParticipants = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            nama:
              data.teamName && data.kategori === "Mix Group (20K)"
                ? data.teamName
                : data.namaLengkap || "Name not available",
            gender: data.gender || "Unknown",
            kota: data.kota || data.alamat || "Unknown",
            country:
              data.negara || data.country || data.nationality || "Unknown",
            statusPembayaran: data.transaction_status || "pending",
            category: data.kategori || "Unknown",
            docRef: doc,
          };
        });
        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            allParticipants,
            totalParticipants: snapshot.size,
            totalFiltered: debouncedSearchTerm
              ? prev.totalFiltered
              : snapshot.size,
          }));
        }
      } catch (err) {
        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            error: `Failed to load all participants: ${err.message}. Please contact admin.`,
            loading: false,
          }));
        }
        console.error(`[ERROR] fetchAllParticipants: ${err.message}`);
      }
    },
    [debouncedSearchTerm]
  );

  const fetchEventData = useCallback(async () => {
    try {
      const eventRef = doc(db, "semuaKegiatan", eventId);
      const eventDoc = await getDoc(eventRef);
      if (!eventDoc.exists()) {
        throw new Error(`Document ${eventId} not found in semuaKegiatan`);
      }

      const eventData = eventDoc.data();

      const eventKey = eventData.eventId || eventId;
      let collectionName = null;

      try {
        const eventsDocRef = doc(db, "events", eventKey);
        const eventsDoc = await getDoc(eventsDocRef);
        if (eventsDoc.exists()) {
          const eventsData = eventsDoc.data() || {};
          if (eventsData.collectionName) {
            collectionName = eventsData.collectionName;
          } else {
            collectionName = `pendaftaran${eventKey}`;
            console.warn(
              `[WARN] fetchEventData: events/${eventKey} exists but no collectionName field; using deterministic collectionName='${collectionName}'`
            );
          }
        } else {
          collectionName = `pendaftaran${eventKey}`;
          console.warn(
            `[WARN] fetchEventData: events/${eventKey} not found; using deterministic collectionName='${collectionName}'`
          );
        }
      } catch (e) {
        collectionName = `pendaftaran${eventId}`;
        console.error(
          `[ERROR] fetchEventData: failed reading events/${eventId}, using fallback collectionName='${collectionName}'`,
          e.message
        );
      }

      const rawCategories = Array.isArray(eventData.categories)
        ? eventData.categories
        : [];
      const categoriesList = rawCategories
        .map((c) => {
          if (!c) return null;
          if (typeof c === "string") return c;
          return c.categoryName || c.category || null;
        })
        .filter(Boolean);

      const initialSelectedCategory =
        category && categoriesList.includes(category) ? category : "all";

      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          eventTitle: eventData.title || eventId,
          eventType: eventData.type || "",
          collectionName,
          loading: true,
          categories: categoriesList,
          selectedCategory: initialSelectedCategory,
        }));
      }
    } catch (err) {
      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          error: `Failed to load event data: ${err.message}`,
          loading: false,
        }));
      }
      console.error(`[ERROR] fetchEventData: ${err.message}`);
    }
  }, [eventId, category]);

  useEffect(() => {
    fetchEventData();
  }, [fetchEventData]);

  // when collectionName + selectedCategory are set, load data (initial + allParticipants)
  useEffect(() => {
    if (!state.collectionName || !state.selectedCategory) return;

    setState((prev) => ({
      ...prev,
      participants: [],
      filteredParticipants: [],
      fullFilteredList: [],
      allParticipants: [],
      lastVisible: null,
      totalParticipants: 0,
      totalFiltered: 0,
      hasMore: true,
      loading: true,
      searchTerm: "",
    }));

    fetchAllParticipants(state.collectionName, state.selectedCategory);
    loadPage(
      state.collectionName,
      null,
      state.sortGenderDirection,
      state.selectedCategory
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.collectionName, state.selectedCategory]);

  // Listen for total participants count for current collection and selectedCategory
  useEffect(() => {
    if (!state.collectionName) return;
    const participantsCollection = collection(db, state.collectionName);
    const baseConstraints = [
      where("transaction_status", "in", ["settlement", "pending"]),
    ];

    buildDistanceOrCategoryConstraint(baseConstraints, state.selectedCategory);

    const q = query(participantsCollection, ...baseConstraints);
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (!isMounted.current) return;
        setState((prev) => ({
          ...prev,
          totalParticipants: snapshot.size,
          totalFiltered: debouncedSearchTerm
            ? prev.totalFiltered
            : snapshot.size,
        }));
      },
      (err) => {
        if (!isMounted.current) return;
        setState((prev) => ({
          ...prev,
          error: `Failed to load total participants: ${err.message}`,
        }));
        console.error(`[ERROR] total onSnapshot: ${err.message}`);
      }
    );
    unsubscribeRef.current.total = unsubscribe;
    return () => unsubscribe();
  }, [state.collectionName, state.selectedCategory, debouncedSearchTerm]);

  // Filtering logic for search (client-side) and keeping filteredParticipants in sync
  useEffect(() => {
    if (!state.collectionName) return;

    if (debouncedSearchTerm) {
      const filteredList = state.allParticipants.filter((p) => {
        const nama = p.nama || "";
        return nama
          .toLowerCase()
          .includes(debouncedSearchTerm.toLowerCase().trim());
      });

      setState((prev) => {
        const initialParticipants = filteredList.slice(0, PAGE_SIZE);
        if (
          JSON.stringify(prev.filteredParticipants) ===
            JSON.stringify(initialParticipants) &&
          prev.totalFiltered === filteredList.length &&
          prev.fullFilteredList.length === filteredList.length
        ) {
          return prev;
        }
        searchPageIndexRef.current = 0;
        return {
          ...prev,
          filteredParticipants: initialParticipants,
          fullFilteredList: filteredList,
          totalFiltered: filteredList.length,
          hasMore: filteredList.length > PAGE_SIZE,
          lastVisible: null,
          searchPageIndex: 0,
          loading: false,
        };
      });
    } else {
      setState((prev) => {
        const newParticipants =
          prev.participants.length > 0 ? prev.participants : [];
        const newLastVisible =
          prev.participants.length > 0 &&
          prev.participants[prev.participants.length - 1]?.docRef
            ? prev.participants[prev.participants.length - 1].docRef
            : null;
        if (
          JSON.stringify(prev.filteredParticipants) ===
            JSON.stringify(newParticipants) &&
          prev.totalFiltered === prev.totalParticipants &&
          prev.lastVisible === newLastVisible
        ) {
          return prev;
        }
        searchPageIndexRef.current = 0;
        return {
          ...prev,
          filteredParticipants: newParticipants,
          fullFilteredList: [],
          totalFiltered: prev.totalParticipants,
          hasMore: newParticipants.length < prev.totalParticipants,
          lastVisible: newLastVisible,
          searchPageIndex: 0,
          loading: false,
        };
      });
    }
  }, [
    debouncedSearchTerm,
    state.allParticipants,
    state.collectionName,
    state.participants,
    state.totalParticipants,
  ]);

  const loadMore = useCallback(() => {
    if (debouncedSearchTerm) {
      if (!state.fullFilteredList.length) return;
      setState((prev) => {
        const currentIndex = searchPageIndexRef.current + 1;
        const startIndex = currentIndex * PAGE_SIZE;
        const additionalList = prev.fullFilteredList.slice(
          startIndex,
          startIndex + PAGE_SIZE
        );
        if (!additionalList.length) return { ...prev, hasMore: false };
        searchPageIndexRef.current = currentIndex;
        return {
          ...prev,
          filteredParticipants: [
            ...prev.filteredParticipants,
            ...additionalList,
          ],
          hasMore: startIndex + PAGE_SIZE < prev.fullFilteredList.length,
          searchPageIndex: currentIndex,
          lastVisible: null,
        };
      });
    } else {
      if (state.loading || state.loadingMore || !state.hasMore) return;
      let nextLastVisible = state.lastVisible;
      if (!nextLastVisible && state.participants.length > 0) {
        nextLastVisible =
          state.participants[state.participants.length - 1]?.docRef || null;
      }
      if (!nextLastVisible) return;
      loadPage(
        state.collectionName,
        nextLastVisible,
        state.sortGenderDirection,
        state.selectedCategory
      );
    }
  }, [
    debouncedSearchTerm,
    state.fullFilteredList,
    state.lastVisible,
    state.loading,
    state.loadingMore,
    state.collectionName,
    state.sortGenderDirection,
    state.hasMore,
    state.participants,
    state.selectedCategory,
    loadPage,
  ]);

  const handleSearchChange = useCallback((e) => {
    const searchValue = e.target.value || "";
    setState((prev) => {
      searchPageIndexRef.current = 0;
      return {
        ...prev,
        searchTerm: searchValue,
        lastVisible: searchValue ? null : prev.lastVisible,
        hasMore: true,
        searchPageIndex: 0,
      };
    });
  }, []);

  const handleGenderSortToggle = useCallback(() => {
    const newDirection = state.sortGenderDirection === "desc" ? "asc" : "desc";
    setState((prev) => {
      searchPageIndexRef.current = 0;
      return {
        ...prev,
        sortGenderDirection: newDirection,
        lastVisible: null,
        hasMore: true,
        searchPageIndex: 0,
        loading: true,
      };
    });
    loadPage(state.collectionName, null, newDirection, state.selectedCategory);
  }, [
    state.sortGenderDirection,
    state.collectionName,
    loadPage,
    state.selectedCategory,
  ]);

  const handleSelectCategory = useCallback(
    (cat) => {
      if (cat === state.selectedCategory) return;

      setState((prev) => ({
        ...prev,
        selectedCategory: cat,
        participants: [],
        filteredParticipants: [],
        fullFilteredList: [],
        allParticipants: [],
        lastVisible: null,
        totalParticipants: 0,
        totalFiltered: 0,
        hasMore: true,
        loading: true,
        searchTerm: "",
      }));
    },
    [state.selectedCategory]
  );

  const truncate = (str, n) => {
    if (!str) return "";
    return str.length > n ? str.slice(0, n) + "..." : str;
  };

  const rangeText = `${state.filteredParticipants.length} of ${
    debouncedSearchTerm ? state.totalFiltered : state.totalParticipants
  } Participants`;

  const getCategoryLabel = (cat) => {
    if (cat === "all") {
      return "All Categories";
    }
    return cat;
  };

  if (state.error) {
    return (
      <section className="w-full min-h-screen flex items-center justify-center flex-col bg-white text-center px-4">
        <p className="text-lg text-gray-600 mb-4">{state.error}</p>
        <Link
          to={eventId ? `/${eventId.toUpperCase()}` : "/"}
          className="text-slate-800 underline text-base"
        >
          Back to Event List
        </Link>
      </section>
    );
  }

  return (
    <section className="w-full min-h-screen py-20 text-center text-slate-800 flex-col justify-center relative bg-white">
      <div className="relative z-1 container mx-auto px-4 lg:px-8">
        <motion.div
          className="mb-6 flex flex-col gap-4 max-w-7xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          <h3 className="text-lg md:text-xl font-black uppercase text-slate-800 text-center block md:hidden">
            {state.eventType === "seminar"
              ? state.eventTitle
              : `${state.eventTitle} - ${getCategoryLabel(
                  state.selectedCategory
                )}`}
          </h3>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="relative w-full max-w-md">
              <input
                type="text"
                placeholder="Search by name..."
                value={state.searchTerm}
                onChange={handleSearchChange}
                className="input w-full p-2 pl-10 border border-gray-300 rounded-xl text-slate-800 text-sm"
                aria-label="Search participants by name"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
            </div>

            <h3 className="text-lg md:text-xl font-black uppercase text-slate-800 md:text-right hidden md:block">
              {state.eventType === "seminar"
                ? state.eventTitle
                : `${state.eventTitle} - ${getCategoryLabel(
                    state.selectedCategory
                  )}`}
            </h3>
          </div>

          <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 max-md:[scrollbar-width:none] max-md:[&::-webkit-scrollbar]:hidden md:mx-0 md:px-0 md:pb-0">
            <button
              onClick={() => handleSelectCategory("all")}
              className={`h-11 flex-none whitespace-nowrap rounded-full border px-4 text-sm font-medium leading-none transition-colors md:h-auto md:flex-initial md:whitespace-normal md:px-4 md:py-1 md:font-normal md:leading-normal ${
                state.selectedCategory === "all"
                  ? "bg-slate-800 text-white"
                  : "bg-white text-slate-800"
              }`}
            >
              <span className="sm:hidden">All</span>
              <span className="hidden sm:inline">All Categories</span>
            </button>

            {state.categories.map((catName) => (
              <button
                key={catName}
                onClick={() => handleSelectCategory(catName)}
                className={`h-11 flex-none whitespace-nowrap rounded-full border px-4 text-sm font-medium leading-none transition-colors md:h-auto md:flex-initial md:whitespace-normal md:px-3 md:py-1 md:font-normal md:leading-normal ${
                  state.selectedCategory === catName
                    ? "bg-slate-800 text-white"
                    : "bg-white text-slate-800"
                }`}
              >
                {catName}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="bg-white rounded-2xl border border-gray-200 text-left relative overflow-hidden max-w-7xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          <div className="overflow-x-auto">
            <table className="w-full table-auto divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Name
                  </th>
                  <th
                    className="px-4 py-3 text-left md:text-center text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:text-slate-800 hidden md:table-cell"
                    onClick={handleGenderSortToggle}
                    aria-label="Sort by gender"
                  >
                    <span className="flex items-center md:justify-center">
                      Gender
                      {state.sortGenderDirection && (
                        <FontAwesomeIcon
                          icon={
                            state.sortGenderDirection === "asc"
                              ? faSortUp
                              : faSortDown
                          }
                          className="ml-1 text-gray-500"
                        />
                      )}
                    </span>
                  </th>
                  <th className="px-4 py-3 text-left md:text-center text-xs font-medium text-gray-600 uppercase tracking-wider hidden md:table-cell">
                    Country
                  </th>
                  <th className="px-4 py-3 text-left md:text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                    City
                  </th>
                  <th className="px-4 py-3 text-left md:text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <SkeletonRow key={index} />
                  ))
                ) : state.filteredParticipants.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-4 text-center text-sm text-gray-600"
                    >
                      {state.searchTerm
                        ? "No participants match your search."
                        : "No participants registered yet."}
                    </td>
                  </tr>
                ) : (
                  state.filteredParticipants.map((participant) => (
                    <tr
                      key={participant.id}
                      className="border-b border-gray-200"
                    >
                      <td
                        className={`px-4 py-4 text-sm whitespace-nowrap md:whitespace-normal ${
                          participant.gender === "Female"
                            ? "text-slate-800"
                            : "text-gray-600"
                        }`}
                      >
                        {participant.nama ? (
                          <>
                            {/* Mobile: truncated to 20 characters */}
                            <span className="block md:hidden">
                              {truncate(participant.nama, 13).toUpperCase()}
                            </span>
                            {/* Desktop: full name */}
                            <span className="hidden md:block">
                              {participant.nama.toUpperCase()}
                            </span>
                          </>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 hidden md:table-cell md:text-center">
                        {participant.gender || "-"}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 hidden md:table-cell md:text-center">
                        {participant.country || "-"}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 md:text-center">
                        {participant.kota || "-"}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {participant.statusPembayaran === "settlement" ? (
                          <FontAwesomeIcon
                            icon={faCheckCircle}
                            className="text-green-600 mx-auto block w-4 h-4"
                            title="Paid / Lunas"
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={faClock}
                            className="text-yellow-500 mx-auto block w-4 h-4"
                            title="Pending / Menunggu Pembayaran"
                          />
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end items-center px-4 py-2">
            <span className="text-sm text-gray-600 mr-4">{rangeText}</span>
            <button
              onClick={loadMore}
              disabled={state.loading || state.loadingMore || !state.hasMore}
              className={`px-3 py-1 rounded-lg transition-colors duration-200 flex items-center gap-1 ${
                state.loading || state.loadingMore || !state.hasMore
                  ? "bg-gray-200 text-gray-600 cursor-not-allowed"
                  : "bg-slate-800 text-white hover:bg-cyan-700"
              }`}
              aria-label={
                state.hasMore
                  ? "Load more participants"
                  : "End of participant list"
              }
            >
              <span>{state.hasMore ? "Load more" : "End of list"}</span>
              <FontAwesomeIcon icon={faChevronDown} />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
});
